import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { cva, type VariantProps } from 'class-variance-authority';
import classNames from 'classnames';
import React, {
  ComponentType,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Link, useLocation } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';
import { useAuth } from '~/hooks/useAuth';
import { useBreakpoint } from '~/hooks/useBreakpoint';
import { useScrollTrigger } from '~/hooks/useScrollTrigger';
import {
  getDetailVisibilityDetection,
  isCreateOutletPath,
} from './fabVisibility';
import styles from './FloatingActionButton.module.css';
import { Button } from './ui/buttons/Button';

const floatingActionButtonStyles = cva(
  [
    'bg-brand',
    'shadow-md',
    'flex gap-2',
    'items-center',
    'justify-center',
    'font-medium',
    'pointer-events-auto',
    'z-50',
    'transition-all',
    'duration-300',
    'ease-standard',
  ],
  {
    variants: {
      hasText: {
        true: [
          'w-fit',
          'h-auto',
          'min-w-[38px]',
          'px-4',
          'py-3',
          'rounded-full',
        ],
        false: ['w-12', 'h-12', 'p-0', 'rounded-full'],
      },
      hierarchy: {
        primary: [
          'bg-brand text-white hover:bg-brand/90 hover:-translate-y-0.5 shadow hover:shadow-xl active:bg-brand/80 active:scale-95 active:shadow-sm',
        ],
        secondary: [
          'bg-white outline-brand hover:shadow-xl shadow hover:-translate-y-0.5 active:bg-surface-container-bright active:scale-95 active:shadow-sm',
          'border border-brand/30',
        ],
      },
      buttonType: {
        single: ['right-5 lg:right-10'],
        singleNavResponsive: ['right-5 lg:right-10'],
        complex: [
          'bottom-[100px]',
          'left-1/2',
          'transform',
          '-translate-x-1/2',
          'lg:hidden',
        ],
      },
      anchor: {
        viewport: ['fixed', 'z-[60]'],
        container: ['absolute', 'z-40'],
      },
      position: {
        default: ['bottom-5', 'right-5', 'lg:right-10'],
        navVisible: ['bottom-28', 'right-5', 'lg:right-10'],
        navHidden: ['bottom-5', 'right-5', 'lg:right-10'],
      },
    },
    defaultVariants: {
      hasText: false,
      buttonType: 'single',
      position: 'default',
      anchor: 'viewport',
    },
  }
);

type ActionConfig = {
  to?: string;
  label: string;
  icon?: ReactNode;
  onClick?: () => void;
};

type Props = {
  actions?: Array<string | ActionConfig>;
  rule?: string;
  hierarchy?: 'primary' | 'secondary';
  inTabsContext?: boolean;
  anchor?: 'viewport' | 'container';
  /** If provided, the FAB will be portaled into the target element */
  targetSelector?: string;
  /** Hide the FAB when a create outlet (path includes /new) is open */
  hideWhenCreateOutletOpen?: boolean;
  /** Control data-usetiful attribute in the underlying FAB (defaults to empty string there) */
  dataUsetiful?: string;
  /** Explicitly control whether the FAB should be hidden when viewing a detail page */
  isViewingDetail?: boolean;
} & VariantProps<typeof floatingActionButtonStyles>;

// Resolve a portal target by CSS selector and expose readiness
const usePortalTarget = (targetSelector: string | undefined) => {
  const [portalTarget, setPortalTarget] = useState<Element | null>(null);
  const [isTargetReady, setIsTargetReady] = useState(false);

  useEffect(() => {
    if (!targetSelector || typeof document === 'undefined') {
      setPortalTarget(null);
      setIsTargetReady(true);
      return;
    }

    let observer: MutationObserver | null = null;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const findTarget = () => document.querySelector(targetSelector);

    const checkTarget = () => {
      const target = findTarget();
      if (target) {
        setPortalTarget(target);
        setIsTargetReady(true);
        return true;
      }
      return false;
    };

    if (checkTarget()) {
      return () => {
        if (observer) observer.disconnect();
        if (timeoutId) clearTimeout(timeoutId);
      };
    }

    timeoutId = setTimeout(() => {
      if (!checkTarget()) {
        observer = new MutationObserver(() => {
          if (checkTarget()) {
            observer?.disconnect();
          }
        });
        observer.observe(document.body, { childList: true, subtree: true });
      }
    }, 300);

    return () => {
      if (observer) observer.disconnect();
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [targetSelector]);

  return { portalTarget, isTargetReady } as const;
};

// Compute a right offset so a fixed-position FAB visually aligns with a target element
const useAlignedRightOffset = (
  portalTarget: Element | null,
  isMobile: boolean
) => {
  const [computedRightOffsetPx, setComputedRightOffsetPx] = useState<
    number | null
  >(null);

  useEffect(() => {
    if (!portalTarget || isMobile) {
      setComputedRightOffsetPx(null);
      return;
    }

    const isLargeScreen = () =>
      typeof window !== 'undefined' &&
      window.matchMedia('(min-width: 1024px)').matches;

    const getBaseGapPx = () => (isLargeScreen() ? 20 : 24);

    let rafId: number | null = null;

    const compute = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const rect = (portalTarget as Element).getBoundingClientRect();
        const viewportRightToTargetRight = window.innerWidth - rect.right;
        const baseGap = getBaseGapPx();
        const rightPx = Math.max(viewportRightToTargetRight + baseGap, baseGap);
        setComputedRightOffsetPx(rightPx);
        rafId = null;
      });
    };

    compute();

    const resizeObserver = new ResizeObserver(() => compute());
    try {
      resizeObserver.observe(portalTarget as Element);
    } catch {
      // ignore
    }

    let resizeTimeoutId: ReturnType<typeof setTimeout> | null = null;
    let scrollTimeoutId: ReturnType<typeof setTimeout> | null = null;

    const handleResize = () => {
      if (resizeTimeoutId) clearTimeout(resizeTimeoutId);
      resizeTimeoutId = setTimeout(compute, 16);
    };

    const handleScroll = () => {
      if (scrollTimeoutId) clearTimeout(scrollTimeoutId);
      scrollTimeoutId = setTimeout(compute, 16);
    };

    window.addEventListener('resize', handleResize, { passive: true });
    window.addEventListener('scroll', handleScroll, {
      passive: true,
      capture: true,
    });

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      resizeObserver.disconnect();
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll, { capture: true });
      if (resizeTimeoutId) clearTimeout(resizeTimeoutId);
      if (scrollTimeoutId) clearTimeout(scrollTimeoutId);
    };
  }, [portalTarget, isMobile]);

  return computedRightOffsetPx;
};

export const FloatingActionButton = ({
  dataUsetiful = '',
  actions = ['new'],
  hierarchy = 'primary',
  inTabsContext = false,
  targetSelector,
  hideWhenCreateOutletOpen = true,
  isViewingDetail,
}: Props) => {
  const { can } = useAuth();
  const { pathname } = useLocation();
  const [showMenu, setShowMenu] = useState<boolean>(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { isMobile } = useBreakpoint();
  const [isVisible, setIsVisible] = useState(false);
  const [isLoadingJobForm, setIsLoadingJobForm] = useState(false);

  // Reset loading state when form opens (with delay to avoid blinking)
  // Also reset immediately if navigating away from the form route
  useEffect(() => {
    if (!pathname.includes('/new')) {
      setIsLoadingJobForm(false);
      return;
    }
    // Wait for form animation/transition to complete before resetting
    const timer = setTimeout(() => {
      setIsLoadingJobForm(false);
    }, 300); // Match typical transition duration
    return () => clearTimeout(timer);
  }, [pathname]);

  // Observe global outlet open state (any view)
  const [isOutletOpenGlobal, setIsOutletOpenGlobal] = useState(false);
  // Track whether THIS FAB is rendered within the outlet container
  const [isWithinOutlet, setIsWithinOutlet] = useState(false);
  // Store ref to the FAB element for re-checking outlet containment
  const fabElementRef = useRef<HTMLButtonElement | HTMLAnchorElement | null>(
    null
  );

  useEffect(() => {
    if (typeof document === 'undefined') return;

    let observer: MutationObserver | null = null;

    const check = () => {
      const el = document.querySelector('[data-outlet]');
      setIsOutletOpenGlobal(!!el);
    };

    // Initial check
    check();

    observer = new MutationObserver(() => check());
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      if (observer) observer.disconnect();
    };
  }, []);

  const { portalTarget, isTargetReady } = usePortalTarget(targetSelector);
  const computedRightOffsetPx = useAlignedRightOffset(portalTarget, isMobile);

  // Check if FAB is within outlet - re-run when outlet state changes or element ref updates
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const inOutletByRoot = !!fabElementRef.current?.closest?.('[data-outlet]');
    const inOutletByPortal = !!portalTarget?.closest?.('[data-outlet]');
    setIsWithinOutlet(inOutletByRoot || inOutletByPortal);
  }, [isOutletOpenGlobal, portalTarget]);

  // Store ref to FAB element and trigger outlet check
  const setFabRef = (el: HTMLButtonElement | HTMLAnchorElement | null) => {
    fabElementRef.current = el;
    if (typeof document === 'undefined') return;
    const inOutletByRoot = !!el?.closest?.('[data-outlet]');
    const inOutletByPortal = !!portalTarget?.closest?.('[data-outlet]');
    setIsWithinOutlet(inOutletByRoot || inOutletByPortal);
  };

  // Fade-in after short delay to avoid visible repositioning on mount
  useEffect(() => {
    setIsVisible(false);
    const t = setTimeout(() => setIsVisible(true), 300);
    return () => clearTimeout(t);
  }, []);

  // Use scroll trigger for nav-responsive positioning (only for main views on mobile)
  const isNavHidden = useScrollTrigger({
    enabled: !inTabsContext && isMobile, // Only enable for main views on mobile
  });

  // Hide FAB when a create outlet is open (e.g., /.../new)
  const isCreateOutletOpen =
    hideWhenCreateOutletOpen &&
    isOutletOpenGlobal &&
    isCreateOutletPath(pathname);

  const renderIcon = (icon: ReactNode, className?: string) => {
    // If loading job form, show spinner instead of icon
    if (isLoadingJobForm) {
      return (
        <svg
          className={classNames('h-5 w-5 animate-spin', className)}
          xmlns='http://www.w3.org/2000/svg'
          fill='none'
          viewBox='0 0 24 24'
        >
          <circle
            className='opacity-25'
            cx='12'
            cy='12'
            r='10'
            stroke='currentColor'
            strokeWidth='4'
          ></circle>
          <path
            className='opacity-75'
            fill='currentColor'
            d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
          ></path>
        </svg>
      );
    }

    if (icon) {
      // Handle React components (like PencilIcon)
      if (typeof icon === 'function') {
        const IconComponent = icon as ComponentType<{ className?: string }>;
        return (
          <IconComponent
            className={classNames(className, {
              'text-brand':
                (hierarchy === 'secondary' || isOutletOpenGlobal) &&
                !isWithinOutlet,
            })}
          />
        );
      }

      // Handle JSX elements (like <FontAwesomeIcon icon={faPlus} />)
      if (React.isValidElement(icon)) {
        return React.cloneElement(icon, {
          className: classNames(icon.props?.className, className, {
            'text-brand':
              (hierarchy === 'secondary' || isOutletOpenGlobal) &&
              !isWithinOutlet,
          }),
        });
      }

      // Handle any other ReactNode
      return icon;
    }

    // Fallback to plus icon
    return (
      <FontAwesomeIcon
        icon={faPlus}
        className={classNames(className, {
          'text-brand':
            (hierarchy === 'secondary' || isOutletOpenGlobal) &&
            !isWithinOutlet,
        })}
      />
    );
  };

  // Single action mode
  const renderSingleAction = (action: string | ActionConfig) => {
    let actionConfig: ActionConfig;

    if (typeof action === 'string') {
      actionConfig = {
        to: action,
        label: action === 'new' ? 'New' : action,
        icon: <FontAwesomeIcon icon={faPlus} />,
      };

      if (action === 'new') {
        const createResource = pathname.split('/')[1] + '.create';
        if (!can(createResource)) return null;
      }
    } else {
      actionConfig = {
        ...action,
        icon: action.icon ?? <FontAwesomeIcon icon={faPlus} />,
      };
    }

    const hasText = !!(
      actionConfig.label && actionConfig.label.trim().length > 0
    );
    const buttonType = inTabsContext ? 'single' : 'singleNavResponsive';
    const position = inTabsContext
      ? 'default'
      : isMobile && isNavHidden
      ? 'navHidden'
      : isMobile
      ? 'navVisible'
      : 'default';

    const shouldAlignToTarget = !!(
      targetSelector &&
      portalTarget &&
      isTargetReady &&
      !isMobile
    );
    // Always use fixed positioning - we align to target by calculating right offset
    // The useAlignedRightOffset hook computes the correct right value based on target's right edge
    const finalAnchor = 'viewport';
    const demotedByOutlet = !isWithinOutlet && isOutletOpenGlobal;
    const effectiveHierarchy =
      hierarchy === 'secondary' || demotedByOutlet ? 'secondary' : 'primary';

    let buttonClasses = floatingActionButtonStyles({
      hasText,
      buttonType,
      position,
      hierarchy: effectiveHierarchy,
      anchor: finalAnchor,
    });

    // Determine visibility based on detail page detection and mobile/outlet state
    const { shouldHideParentFab, shouldHideOnMobile } =
      getDetailVisibilityDetection(
        actionConfig.label || '',
        isViewingDetail,
        pathname,
        inTabsContext,
        isMobile,
        isOutletOpenGlobal,
        isWithinOutlet
      );

    buttonClasses = classNames(
      buttonClasses,
      'duration-200 ease-standard transition-opacity transition-background-color',
      {
        'opacity-0': !isVisible,
        'opacity-100': isVisible,
        hidden: isCreateOutletOpen || shouldHideOnMobile,
      }
    );

    if (targetSelector && !isTargetReady) return null;

    // Position relative to container if targetSelector provided, otherwise use standard positioning
    const inlineRight =
      shouldAlignToTarget && computedRightOffsetPx !== null
        ? { right: computedRightOffsetPx }
        : undefined;

    const ButtonInner = (
      <>
        {hasText ? (
          <>
            {renderIcon(actionConfig.icon!, 'w-4 h-4')}
            <span
              className={classNames({
                'text-secondary':
                  (hierarchy === 'secondary' || isOutletOpenGlobal) &&
                  !isWithinOutlet,
              })}
            >
              {actionConfig.label}
            </span>
          </>
        ) : (
          renderIcon(actionConfig.icon!, 'w-5 h-5')
        )}
      </>
    );

    if (actionConfig.onClick) {
      return (
        <button
          ref={setFabRef}
          onClick={actionConfig.onClick}
          disabled={isLoadingJobForm}
          className={buttonClasses}
          style={inlineRight}
          aria-label={actionConfig.label}
          data-usetiful={dataUsetiful}
        >
          {ButtonInner}
        </button>
      );
    }

    const computedStyles = {
      ...(inlineRight ?? {}),
      transitionProperty: 'opacity, background-color, bottom, top, transform',
      transitionDuration: '200ms',
      transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
    } as const;

    return (
      <Link
        to={actionConfig.to!}
        ref={setFabRef}
        className={classNames(buttonClasses, {
          'pointer-events-none opacity-50': isLoadingJobForm,
        })}
        style={computedStyles}
        aria-label={actionConfig.label}
        data-usetiful={dataUsetiful}
        onClick={(e) => {
          if (isLoadingJobForm) {
            e.preventDefault();
            return;
          }
          if (actionConfig.to?.includes('/new') || actionConfig.to === 'new') {
            setIsLoadingJobForm(true);
          }
        }}
      >
        {ButtonInner}
      </Link>
    );
  };

  const renderMultiAction = () => {
    return (
      <div
        className={classNames(
          styles.FloatingActionButton,
          'pointer-events-auto lg:hidden',
          {
            [styles.open]: showMenu,
          }
        )}
      >
        <span
          className={styles.toggle}
          onClick={() => setShowMenu((s) => !s)}
          role='button'
          aria-label='Toggle action menu'
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setShowMenu((s) => !s);
            }
          }}
        >
          <FontAwesomeIcon icon={faPlus} />
        </span>
        <CSSTransition
          in={showMenu}
          timeout={300}
          classNames='slide'
          nodeRef={menuRef}
          unmountOnExit
        >
          <div className={styles.menu} ref={menuRef}>
            {actions
              .map((a) =>
                typeof a === 'string'
                  ? { to: a, label: a }
                  : { to: a.to || '#', label: a.label }
              )
              .map(({ to, label }) => (
                <Button
                  dataUsetiful={dataUsetiful}
                  key={to}
                  as={Link}
                  to={to}
                  color='primary'
                  rounded
                  onClick={() => setShowMenu(false)}
                >
                  {label}
                </Button>
              ))}
          </div>
        </CSSTransition>
      </div>
    );
  };

  if (actions.length === 1) return renderSingleAction(actions[0]);
  return renderMultiAction();
};
